import React, { useLayoutEffect, useRef } from "react";
import type { ChatVoice } from "../../services/rtkApi/agentApi";

export interface VirtualizedChatItem {
  id: string;
  content: string;
  sender: string;
  senderType: "user" | "agent" | "self";
  timestamp: string;
  /** shared message id reactions attach to (absent on legacy messages without one) */
  mid?: string;
  /** present when this message is a voice note */
  voice?: ChatVoice;
}

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** true when two timestamps fall on the same local calendar day */
function isSameLocalDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
  return startOfLocalDay(da) === startOfLocalDay(db);
}

/** "Today" / "Yesterday" / "Mon, Jun 2" / "Jun 2, 2025" for a day divider */
function formatDaySeparator(ts: string): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffDays = Math.round(
    (startOfLocalDay(now) - startOfLocalDay(d)) / 86_400_000,
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(
    [],
    sameYear
      ? { weekday: "short", month: "short", day: "numeric" }
      : { year: "numeric", month: "short", day: "numeric" },
  );
}

/** centered day-divider chip shown between messages from different calendar days */
function DaySeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 my-1.5 select-none" aria-hidden="true">
      <div className="flex-1 h-px bg-ink/10" />
      <span className="px-2 py-0.5 text-[10px] md:text-xs font-bold text-gray-500 bg-gray-100 border border-ink/15 rounded-full whitespace-nowrap">
        {label}
      </span>
      <div className="flex-1 h-px bg-ink/10" />
    </div>
  );
}

/**
 * scrollable message column. history is capped (~100) so we use normal document flow
 * instead of absolute virtual rows — pretext row estimates were drifting from real CSS
 * (leading-snug, borders, shrink-wrap) and caused overlapping bubbles on mobile.
 */
export interface VirtualizedMessageListProps {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  items: VirtualizedChatItem[];
  renderItem: (item: VirtualizedChatItem) => React.ReactNode;
  /** optional reaction-chip row rendered on its own line directly under each bubble */
  renderReactions?: (item: VirtualizedChatItem) => React.ReactNode;
  /** optional read-receipt row rendered on its own line under each bubble */
  renderReceipts?: (item: VirtualizedChatItem) => React.ReactNode;
  footer?: React.ReactNode;
  /** unused — kept for API stability with callers */
  compact?: boolean;
}

export function VirtualizedMessageList({
  scrollRef,
  onScroll,
  items,
  renderItem,
  renderReactions,
  renderReceipts,
  footer,
}: VirtualizedMessageListProps) {
  const localRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current =
      localRef.current;
    return () => {
      (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current =
        null;
    };
  }, [scrollRef]);

  return (
    <div
      ref={localRef}
      onScroll={() => onScroll()}
      className="flex-1 overflow-y-auto p-4 min-h-0 relative max-[700px]:p-3"
    >
      <div className="flex flex-col gap-2">
        {items.map((item, i) => {
          const reactions = renderReactions?.(item);
          const receipts = renderReceipts?.(item);
          const prev = items[i - 1];
          const showDay =
            !prev || !isSameLocalDay(prev.timestamp, item.timestamp);
          const dayLabel = showDay ? formatDaySeparator(item.timestamp) : "";
          return (
            <React.Fragment key={item.id}>
              {dayLabel && <DaySeparator label={dayLabel} />}
              <div className="flex flex-col">
                <div
                  className={
                    item.senderType === "self"
                      ? "flex justify-end w-full"
                      : "flex justify-start w-full"
                  }
                >
                  {renderItem(item)}
                </div>
                {reactions}
                {receipts}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {footer}
    </div>
  );
}
